import { Fragment, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, ClockCounterClockwise, PencilSimple, X } from "@phosphor-icons/react";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Breakdown } from "./PayrollPage";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  denied: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-600",
};
const TYPE_LABEL = { pto: "PTO", sick: "Sick", unpaid: "Unpaid" };

const fiscalYearOf = (iso) => {
  if (!iso) return null;
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  return d.getMonth() + 1 >= 6 ? d.getFullYear() : d.getFullYear() - 1;
};
const fyLabel = (fy) => `FY ${fy} (Jun ${fy} – May ${fy + 1})`;

export default function HistoryPage() {
  const { hasPerm } = useAuth();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [fy, setFy] = useState("all");
  const [loading, setLoading] = useState(false);
  const [correctFor, setCorrectFor] = useState(null);
  const [corrForm, setCorrForm] = useState({ amount: "", reason: "" });
  const [corrBusy, setCorrBusy] = useState(false);
  const [expandedPeriod, setExpandedPeriod] = useState(null);

  const canAdjust = hasPerm("pay.adjust");

  useEffect(() => {
    if (!hasPerm("pay.view")) return;
    api.get("/history/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const reloadDetail = () => {
    if (!selected) return;
    api.get(`/history/${selected}`).then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setData(null);
    setFy("all");
    setFilter("");
    setExpandedPeriod(null);
    api.get(`/history/${selected}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [selected]);

  const submitCorrection = async () => {
    setCorrBusy(true);
    try {
      await api.post("/payroll/adjustments", {
        user_id: selected,
        amount: parseFloat(corrForm.amount),
        period_start: correctFor.period_start,
        period_end: correctFor.period_end,
        reason: corrForm.reason,
      });
      toast.success("Correction recorded — logged to audit trail");
      setCorrectFor(null);
      reloadDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record correction");
    } finally {
      setCorrBusy(false);
    }
  };

  const removeAdjustment = async (a) => {
    try {
      await api.delete(`/payroll/adjustments/${a.id}`);
      toast.success("Correction removed — logged to audit trail");
      reloadDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove correction");
    }
  };

  if (!hasPerm("pay.view")) return <Navigate to="/" replace />;

  const q = search.toLowerCase();
  const visible = members.filter((m) => !q || m.full_name.toLowerCase().includes(q) || (m.department || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q));

  const fys = data
    ? [...new Set([
        ...data.timeoff.map((r) => fiscalYearOf(r.start_date)),
        ...data.payroll_periods.map((p) => fiscalYearOf(p.pay_date)),
      ].filter(Boolean))].sort((a, b) => b - a)
    : [];

  const inFy = (iso) => fy === "all" || fiscalYearOf(iso) === parseInt(fy);
  const f = filter.toLowerCase();
  const timeoff = data ? data.timeoff.filter((r) => inFy(r.start_date) &&
    (!f || `${TYPE_LABEL[r.type]} ${r.status} ${r.reason || ""} ${r.start_date} ${r.end_date}`.toLowerCase().includes(f))) : [];
  const payroll = data ? data.payroll_periods.filter((p) => inFy(p.pay_date)) : [];

  return (
    <div className="space-y-8">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Records</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
          <ClockCounterClockwise size={30} weight="duotone" className="text-indigo-600" /> History
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Permanent, searchable payroll and time-off records for every member — including deactivated accounts. Filter by fiscal year (Jun 1 – May 31).</p>
      </div>

      <div className="bg-card border border-border rounded-md p-4 sm:p-6 space-y-3">
        <div className="relative max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="history-search-input" placeholder="Search member, department or email…" className="pl-8"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="history-member-list">
          {visible.map((m) => (
            <button key={m.id} data-testid={`history-member-${m.id}`} onClick={() => setSelected(m.id)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors flex items-center gap-1.5 ${
                selected === m.id ? "bg-indigo-600 border-indigo-600 text-white" : "bg-card border-border text-muted-foreground hover:border-indigo-400"}`}>
              {m.full_name}
              {!m.is_active && <span className={`text-[10px] px-1 rounded ${selected === m.id ? "bg-white/20" : "bg-slate-200 text-slate-600"}`}>deactivated</span>}
            </button>
          ))}
          {visible.length === 0 && <p className="text-sm text-muted-foreground">No members match your search.</p>}
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading history…</p>}

      {data && (
        <div className="space-y-6" data-testid="history-detail">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-heading text-xl font-semibold tracking-tight">{data.user.full_name}</h2>
            {!data.user.is_active && <Badge className="bg-slate-100 text-slate-600">deactivated</Badge>}
            <span className="text-xs text-muted-foreground">{data.user.department ? `${data.user.department} · ` : ""}{data.user.pay_type} · {data.user.pay_schedule}{data.user.hire_date ? ` · hired ${fmtDate(data.user.hire_date)}` : ""}</span>
            <div className="ml-auto flex gap-2">
              <Select value={fy} onValueChange={setFy}>
                <SelectTrigger data-testid="history-fy-select" className="h-9 w-52"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All fiscal years</SelectItem>
                  {fys.map((y) => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="bg-card border border-border rounded-md">
            <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">Payroll periods</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]" data-testid="history-payroll-table">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.12em] text-muted-foreground border-b border-border">
                    <th className="text-left px-6 py-2.5 font-semibold">Period</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Pay date</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Base</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Unpaid deduction</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Adjustments</th>
                    <th className="text-right px-6 py-2.5 font-semibold">Gross</th>
                    {canAdjust && <th className="px-3 py-2.5"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {payroll.map((p) => (
                    <Fragment key={p.period_start}>
                    <tr data-testid={`history-period-${p.period_start}`}
                      className="cursor-pointer hover:bg-muted/60 transition-colors"
                      onClick={() => setExpandedPeriod(expandedPeriod === p.period_start ? null : p.period_start)}>
                      <td className="px-6 py-2.5">
                        <span className={`inline-block mr-1.5 text-muted-foreground transition-transform ${expandedPeriod === p.period_start ? "rotate-90" : ""}`}>›</span>
                        {fmtDate(p.period_start)} → {fmtDate(p.period_end)}
                      </td>
                      <td className="px-3 py-2.5">{fmtDate(p.pay_date)}</td>
                      <td className="px-3 py-2.5 text-right">{fmtMoney(p.base_pay)}</td>
                      <td className={`px-3 py-2.5 text-right ${p.unpaid_deduction > 0 ? "text-red-600" : "text-muted-foreground"}`}>{p.unpaid_deduction > 0 ? `−${fmtMoney(p.unpaid_deduction)}` : "—"}</td>
                      <td className={`px-3 py-2.5 text-right ${p.adjustments_total ? (p.adjustments_total > 0 ? "text-emerald-700" : "text-red-600") : "text-muted-foreground"}`}
                        title={(p.adjustments || []).map((a) => `${a.amount > 0 ? "+" : "−"}$${Math.abs(a.amount).toFixed(2)}: ${a.reason}`).join("\n")}>
                        {p.adjustments_total ? `${p.adjustments_total > 0 ? "+" : "−"}${fmtMoney(Math.abs(p.adjustments_total))}` : "—"}
                      </td>
                      <td className="px-6 py-2.5 text-right font-semibold">{fmtMoney(p.gross_estimate)}</td>
                      {canAdjust && (
                        <td className="px-3 py-2.5 text-right">
                          <Button data-testid={`history-correct-btn-${p.period_start}`} size="sm" variant="outline" className="h-7 px-2 text-xs"
                            onClick={(e) => { e.stopPropagation(); setCorrectFor(p); setCorrForm({ amount: "", reason: "" }); }}>
                            <PencilSimple size={12} className="mr-1" /> Correct
                          </Button>
                        </td>
                      )}
                    </tr>
                    {expandedPeriod === p.period_start && (
                      <tr data-testid={`history-period-detail-${p.period_start}`}>
                        <td colSpan={canAdjust ? 7 : 6} className="px-6 sm:px-10 py-4 bg-muted/50">
                          <Breakdown p={p} canAdjust={canAdjust} onRemoveAdjustment={removeAdjustment} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                  {payroll.length === 0 && <tr><td colSpan={canAdjust ? 7 : 6} className="px-6 py-6 text-muted-foreground">No pay periods in this range.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="px-6 py-3 text-xs text-muted-foreground border-t border-border">Click any period to expand its full pay breakdown, corrections and notes. Each period is calculated with the pay rate actually in force at the time — mid-period changes are pro-rated by business day. Commissions are not reflected.</p>
          </div>

          <div className="bg-card border border-border rounded-md">
            <div className="px-6 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-heading font-semibold tracking-tight">Time off ({timeoff.length})</h3>
              <Input data-testid="history-timeoff-filter" placeholder="Filter: type, status, reason…" className="h-9 w-56"
                value={filter} onChange={(e) => setFilter(e.target.value)} />
            </div>
            <div className="divide-y divide-border" data-testid="history-timeoff-list">
              {timeoff.map((r) => (
                <div key={r.id} className="px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{TYPE_LABEL[r.type]} · {fmtDate(r.start_date)} → {fmtDate(r.end_date)} · {r.total_hours}h</p>
                    <p className="text-xs text-muted-foreground">{r.reason && `"${r.reason}" · `}{r.short_notice && "short-notice · "}{r.reviewer_comment && `note: "${r.reviewer_comment}"`}</p>
                  </div>
                  <Badge className={STATUS_STYLE[r.status]}>{r.status}</Badge>
                </div>
              ))}
              {timeoff.length === 0 && <p className="px-6 py-6 text-sm text-muted-foreground">No time-off records in this range.</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-md">
              <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">Payroll adjustments</h3></div>
              <div className="divide-y divide-border" data-testid="history-adjustments-list">
                {data.adjustments.map((a) => (
                  <div key={a.id} className="px-6 py-3 text-sm flex items-start justify-between gap-3">
                    <div>
                      <p className={`font-medium ${a.amount > 0 ? "text-emerald-700" : "text-red-600"}`}>{a.amount > 0 ? "+" : "−"}{fmtMoney(Math.abs(a.amount))} · {fmtDate(a.period_start)} → {fmtDate(a.period_end)}</p>
                      <p className="text-xs text-muted-foreground">"{a.reason}" — {a.created_by_name}, {fmtDate(a.created_at)}</p>
                    </div>
                    {canAdjust && (
                      <button data-testid={`history-remove-adjustment-${a.id}`} title="Remove correction" onClick={() => removeAdjustment(a)}
                        className="text-muted-foreground hover:text-red-600 transition-colors mt-1 shrink-0"><X size={14} /></button>
                    )}
                  </div>
                ))}
                {data.adjustments.length === 0 && <p className="px-6 py-6 text-sm text-muted-foreground">No manual adjustments on record.</p>}
              </div>
            </div>
            <div className="bg-card border border-border rounded-md">
              <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">Pay changes</h3></div>
              <div className="divide-y divide-border" data-testid="history-paychanges-list">
                {data.pay_changes.map((c) => (
                  <div key={c.id} className="px-6 py-3 text-sm">
                    <p className="text-xs text-muted-foreground">{fmtDate(c.timestamp)} — {c.changed_by_name} ({c.source})</p>
                    {c.effective_date && <p className="text-xs">Effective: <strong>{fmtDate(c.effective_date)}</strong></p>}
                    {(c.changes || []).map((ch, i) => (
                      <p key={i} className="text-xs">{ch.field}: <span className="text-muted-foreground">{String(ch.old)}</span> → <strong>{String(ch.new)}</strong></p>
                    ))}
                    {c.note && <p className="text-xs text-muted-foreground mt-0.5">Note: "{c.note}"</p>}
                  </div>
                ))}
                {data.pay_changes.length === 0 && <p className="px-6 py-6 text-sm text-muted-foreground">No pay parameter changes on record.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {!selected && !loading && <p className="text-sm text-muted-foreground">Pick a member above to view their full record.</p>}

      <Dialog open={!!correctFor} onOpenChange={() => setCorrectFor(null)}>
        <DialogContent data-testid="history-correct-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Correct {data?.user?.full_name}'s pay period</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground -mt-1">Period <strong>{correctFor && `${fmtDate(correctFor.period_start)} → ${fmtDate(correctFor.period_end)}`}</strong> (paid {correctFor && fmtDate(correctFor.pay_date)}).</p>
            <div>
              <Label>Correction amount ($) <span className="text-red-500">*</span></Label>
              <Input data-testid="history-correct-amount" type="number" step="0.01" className="mt-1.5" placeholder="e.g. 120 or -45.50"
                value={corrForm.amount} onChange={(e) => setCorrForm({ ...corrForm, amount: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">Positive adds to that period's gross, negative deducts.</p>
            </div>
            <div>
              <Label>Note <span className="text-red-500">*</span></Label>
              <Textarea data-testid="history-correct-note" className="mt-1.5" rows={2}
                placeholder="e.g. Missed overtime on 8/12, duplicate deduction reversed…"
                value={corrForm.reason} onChange={(e) => setCorrForm({ ...corrForm, reason: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">Mandatory — stored on the period forever and logged to the audit trail.</p>
            </div>
            <Button data-testid="history-correct-submit" onClick={submitCorrection}
              disabled={corrBusy || !parseFloat(corrForm.amount) || !corrForm.reason.trim()}
              className="w-full bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">
              {corrBusy ? "Saving…" : "Record correction"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
